<div>
  <!-- Toast for success/error messages -->
  <p-toast></p-toast>

  <!-- Confirmation Dialog for Delete Operations -->
  <p-confirmDialog></p-confirmDialog>

  <!-- Loading Indicator -->
  @if (loading) {
    <div class="my-6 flex flex-col items-center justify-center">
      <!-- Tailwind Spinner -->
      <div class="relative h-14 w-14">
        <div class="absolute h-14 w-14 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
      </div>
      <span class="mt-3 font-medium">{{ 'common.loading' | translate }}</span>
    </div>
  }

  <!-- Error state -->
  @if (error$ | async; as errorMessage) {
    @if (errorMessage) {
      <div class="surface-danger border-round mb-3 p-4 text-white">
        {{ errorMessage }}
      </div>
    }
  }

  <!-- Empty state when no bookings and not loading -->
  @if (!loading && (!paginatedBookings || !paginatedBookings.items.length)) {
    <div class="card">
      <div class="p-5 text-center">
        <app-empty-state
          [title]="'bookings.noBookings'"
          [description]="'bookings.noBookingsDescription'"
        >
        </app-empty-state>
      </div>
    </div>
  }

  <!-- Table with bookings - only shown when there are bookings and not loading -->
  @if (!loading && paginatedBookings && paginatedBookings.items.length > 0) {
    <div class="">
      <p-table
        #dt
        [value]="paginatedBookings.items"
        [stripedRows]="true"
        styleClass="p-datatable-sm"
        [rowHover]="true"
        [tableStyle]="{ 'min-width': '60rem' }"
        responsiveLayout="scroll"
        (sortFunction)="onSort($event)"
        [customSort]="true"
        dataKey="id"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="vertical-align: middle">
              {{ 'bookings.flat' | translate }}
            </th>
            <th
              [pSortableColumn]="'startDate'"
              style="vertical-align: middle"
            >
              <div class="flex items-center justify-between">
                {{ 'bookings.startDate' | translate }}&nbsp;<span class="ml-1">
                  @if (sortState.field === 'startDate' && sortState.direction === 'asc') {
                    <i class="pi pi-sort-up"></i>
                  }
                  @if (sortState.field === 'startDate' && sortState.direction === 'desc') {
                    <i class="pi pi-sort-down"></i>
                  }
                  @if (sortState.field !== 'startDate') {
                    <i class="pi pi-sort text-300"></i>
                  }
                </span>
              </div>
            </th>
            <th
              [pSortableColumn]="'endDate'"
              style="vertical-align: middle"
            >
              <div class="flex items-center justify-between">
                {{ 'bookings.endDate' | translate }}
                <span class="ml-1">
                  @if (sortState.field === 'endDate' && sortState.direction === 'asc') {
                    <i class="pi pi-sort-up"></i>
                  }
                  @if (sortState.field === 'endDate' && sortState.direction === 'desc') {
                    <i class="pi pi-sort-down"></i>
                  }
                  @if (sortState.field !== 'endDate') {
                    <i class="pi pi-sort text-300"></i>
                  }
                </span>
              </div>
            </th>
            <th
              [pSortableColumn]="'guestName'"
              style="vertical-align: middle"
            >
              <div class="flex justify-between">
                {{ 'bookings.guest' | translate }}
                <span class="ml-1">
                  @if (sortState.field === 'guestName' && sortState.direction === 'asc') {
                    <i class="pi pi-sort-up"></i>
                  }
                  @if (sortState.field === 'guestName' && sortState.direction === 'desc') {
                    <i class="pi pi-sort-down"></i>
                  }
                  @if (sortState.field !== 'guestName') {
                    <i class="pi pi-sort text-300"></i>
                  }
                </span>
              </div>
            </th>
            <th
              [pSortableColumn]="'guestCount'"
              style="vertical-align: middle"
            >
              <div class="flex justify-between">
                {{ 'bookings.guestCount' | translate }}
                <span class="ml-1">
                  @if (sortState.field === 'guestCount' && sortState.direction === 'asc') {
                    <i class="pi pi-sort-up"></i>
                  }
                  @if (sortState.field === 'guestCount' && sortState.direction === 'desc') {
                    <i class="pi pi-sort-down"></i>
                  }
                  @if (sortState.field !== 'guestCount') {
                    <i class="pi pi-sort text-300"></i>
                  }
                </span>
              </div>
            </th>
            <th
              [pSortableColumn]="'status'"
              style="vertical-align: middle"
            >
              <div class="flex justify-between">
                {{ 'bookings.status' | translate }}
                <span class="ml-1">
                  @if (sortState.field === 'status' && sortState.direction === 'asc') {
                    <i class="pi pi-sort-up"></i>
                  }
                  @if (sortState.field === 'status' && sortState.direction === 'desc') {
                    <i class="pi pi-sort-down"></i>
                  }
                  @if (sortState.field !== 'status') {
                    <i class="pi pi-sort text-300"></i>
                  }
                </span>
              </div>
            </th>
            <th style="vertical-align: middle">{{ 'common.actions' | translate }}</th>
          </tr>
        </ng-template>

        <ng-template
          pTemplate="body"
          let-booking
        >
          <tr
            (click)="onRowClick(booking.id)"
            class="cursor-pointer"
          >
            <!-- Flat Info -->
            <td>
              <app-flat-info
                [flatInfo]="booking.flatInfo"
                [style]="'dot'"
              ></app-flat-info>
            </td>

            <!-- Check-in Date -->
            <td class="whitespace-nowrap">{{ booking.startDate | date: 'YYYY-MM-dd' }}</td>

            <!-- Check-out Date -->
            <td class="whitespace-nowrap">{{ booking.endDate | date: 'YYYY-MM-dd' }}</td>

            <!-- Guest Name -->
            <td>{{ booking.guestName }}</td>

            <!-- Guest Count -->
            <td>
              <span class="inline-flex items-center">
                <i class="pi pi-users mr-1 text-gray-500"></i>
                {{ booking.guestCount }}
              </span>
            </td>

            <!-- Status -->
            <td>
              <div class="flex flex-wrap items-center gap-1.5">
                <app-badge-info [badge]="booking.status"></app-badge-info>
                <app-chain-marks [marks]="booking.chain"></app-chain-marks>
              </div>
            </td>

            <!-- Actions -->
            <td>
              <div class="flex gap-2">
                <button
                  pButton
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button"
                  (click)="onRowClick(booking.id)"
                  title="{{ 'common.view' | translate }}"
                ></button>
                <button
                  pButton
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button p-button-danger"
                  (click)="confirmDelete(booking.id, $event)"
                  title="{{ 'common.delete' | translate }}"
                ></button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="7">
              @if (loading) {
                <!-- Loading indicator inside the table -->
                <div class="my-6 flex flex-col items-center justify-center">
                  <div class="relative h-10 w-10">
                    <div class="absolute h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
                  </div>
                </div>
              } @else {
                <app-empty-state
                  [title]="'bookings.emptyTitle' | translate"
                  [description]="'bookings.emptyMessage' | translate"
                >
                </app-empty-state>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Pagination controls -->
      <app-pagination
        [pageSize]="pageSize"
        [currentPage]="currentPage"
        [totalItems]="paginatedBookings.totalCount"
        (pageChange)="onPaginationChange($event)"
      >
      </app-pagination>
    </div>
  }
</div>
